'use client';

import { useContext, useMemo } from 'react';

import type { PartiallyRequiredType } from '../../../types/partially-required';
import { ComboboxRootContext } from '../context/combobox-root.context';
import type { ComboboxContentPropsInterface } from '../types/combobox-content.types';
import type { ComboboxGroupType, ComboboxOptionType } from '../types/combobox-root.types';
import { isGroup } from '../utils/is-group';

/**
 * Hook for rendering combobox option list based on the current state
 *
 * This hook handles the rendering logic for different states of a combobox:
 * - Loading state when data is being fetched
 * - Empty state when no options match the current filter
 * - Options rendering for both grouped and non-grouped options
 *
 * It consumes the ComboboxRootContext to access shared state and returns
 * the appropriate content based on the current state.
 *
 * @param props - Rendering functions and configuration for the combobox content
 * @returns JSX elements
 */
export const useComboboxOptionsList = ({
  renderLoadingState,
  renderEmptyState,
  renderGroup,
  renderHeading,
  renderOption,
  renderOptionLabel,
  renderOptionMedia,
  renderOptionMeta,
}: PartiallyRequiredType<
  ComboboxContentPropsInterface,
  | 'renderEmptyState'
  | 'renderGroup'
  | 'renderHeading'
  | 'renderLoadingState'
  | 'renderOption'
  | 'renderOptionLabel'
  | 'renderOptionMedia'
  | 'renderOptionMeta'
>) => {
  // Access the combobox context to get current state
  const { combobox, loading } = useContext(ComboboxRootContext);

  return useMemo(() => {
    // Handle loading state when data is being fetched
    if (loading) {
      return renderLoadingState({});
    }

    // Get filtered options with fallback to empty array if combobox is not initialized
    const options = combobox.filteredOptions || [];

    // Get option label highlight pattern
    const highlightPattern = combobox.highlightPattern;

    // Handle empty state when no options match the current filter
    if (!options.length) {
      return renderEmptyState({ children: 'No matches found' });
    }

    // Common props shared between group and option rendering
    const optionRenderProps = {
      highlightPattern,
      renderOptionLabel,
      renderOptionMedia,
      renderOptionMeta,
    };

    // Handle mixed array of options and groups
    return options.map((item, index) => {
      // Check if this item is a group by looking for the options property
      if (isGroup(item)) {
        // It's a group - render as group
        return renderGroup({
          key: `group-${index}`,
          group: item as ComboboxGroupType,
          renderHeading,
          renderOption,
          ...optionRenderProps,
        });
      }
      // It's a regular option - render as option
      return renderOption({
        key: `option-${index}`,
        option: item as ComboboxOptionType,
        ...optionRenderProps,
      });
    });
  }, [
    loading,
    combobox.filteredOptions,
    combobox.highlightPattern,
    renderLoadingState,
    renderEmptyState,
    renderGroup,
    renderHeading,
    renderOption,
    renderOptionLabel,
    renderOptionMedia,
    renderOptionMeta,
  ]);
};
